import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categoryLinks } from '@/mocks/categories';

const guideLinks = [
  { label: 'Guida Completa 2026', slug: 'guida-itaobuy-spreadsheet', icon: 'ri-book-open-line' },
  { label: 'Sicurezza Acquisti', slug: 'sicurezza-acquisti-online', icon: 'ri-shield-check-line' },
  { label: 'Guida Principianti', slug: 'guida-principianti', icon: 'ri-user-smile-line' },
  { label: 'Migliori Prodotti', slug: 'migliori-prodotti-2026', icon: 'ri-star-line' },
  { label: 'Come Usare', slug: 'come-usare-itaobuy', icon: 'ri-question-answer-line' },
  { label: 'Confronto Prezzi', slug: 'confronto-prezzi-qualita', icon: 'ri-scales-line' },
  { label: 'QC Photos Guide', slug: 'qc-photos-guida-completa', icon: 'ri-camera-line' },
  { label: 'Recensione 2026', slug: 'recensione-completa-2026', icon: 'ri-chat-check-line' },
  { label: 'Categorie Prodotti', slug: 'guida-categorie-prodotti', icon: 'ri-grid-line' },
  { label: 'Risparmiare', slug: 'risparmiare-acquisti-online', icon: 'ri-money-euro-circle-line' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [catOpen, setCatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) {
        setGuideOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="text-xl md:text-2xl font-heading font-bold text-foreground-950 whitespace-nowrap cursor-pointer">
          Itaobuy <span className="text-primary-500">Spreadsheet</span>
        </Link>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-4-line text-sm"></i>
            </span>
            Home
          </Link>

          <div ref={guideRef} className="relative">
            <button
              onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line text-sm"></i>
              </span>
              Guida Shopping
              <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${guideOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {guideOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-lg border border-background-200/70 max-h-80 overflow-y-auto shadow-lg">
                {guideLinks.map((link) => (
                  <Link
                    key={link.slug}
                    to={`/articolo/${link.slug}`}
                    onClick={() => setGuideOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${link.icon} text-base`}></i>
                    </span>
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div ref={catRef} className="relative">
            <button
              onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-secondary-100 text-foreground-800 text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-grid-line text-sm"></i>
              </span>
              {t('nav.selectCategory', 'Categorie')}
              <i className={`ri-arrow-down-s-line transition-transform duration-200 text-xs ${catOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {catOpen && (
              <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-lg border border-background-200/70 max-h-96 overflow-y-auto shadow-lg">
                {categoryLinks.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                      <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('buttons.itaobuySheet', 'itaobuy sheet')}
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4 flex flex-col gap-2 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-home-4-line text-base"></i>
            </span>
            Home
          </Link>

          <button
            onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
            className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center gap-3">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-open-line text-base"></i>
              </span>
              Guida Shopping
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {mobileGuideOpen && (
            <div className="pl-4 space-y-1 border-l-2 border-background-200/70 ml-3">
              {guideLinks.map((link) => (
                <Link
                  key={link.slug}
                  to={`/articolo/${link.slug}`}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${link.icon} text-sm`}></i>
                  </span>
                  {link.label}
                </Link>
              ))}
            </div>
          )}

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg bg-secondary-100 text-sm font-medium text-foreground-800 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center gap-3">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-grid-line text-base"></i>
              </span>
              Categorie
            </span>
            <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {mobileCatOpen && (
            <div className="pl-4 space-y-1 border-l-2 border-background-200/70 ml-3">
              {categoryLinks.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 overflow-hidden flex-shrink-0">
                    <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="mt-3 px-4 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            itaobuy sheet
          </a>
        </div>
      )}
    </nav>
  );
}